// v3.99c.73 — StrainTrendingStrip
// Compact "Now Trending Around This Strain" sliver that surfaces the 2-3
// latest Forge Feed items related to a strain by name / breeder / family.
//
// Mounted on LineageDetail.jsx below the main strain info, above the
// final CTA. Reuses FeedCard styling so logged-out users get the same
// branded fallbacks for items with no images.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, ArrowRight, Sparkles } from "lucide-react";
import { api } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import FeedCard from "./feed/FeedCard";

export default function StrainTrendingStrip({ slug }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [matchedVia, setMatchedVia] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!slug) return undefined;
    let alive = true;
    setLoaded(false);
    api.get(`/feed/related/strain/${slug}`, { params: { limit: 3 } })
      .then((r) => {
        if (!alive) return;
        setItems(r.data?.items || []);
        setMatchedVia(r.data?.matched_via || null);
        setLoaded(true);
      })
      .catch(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [slug]);

  if (!loaded) return null;

  const matchLabel = (() => {
    if (!matchedVia || items.length === 0) return null;
    return {
      strain: "Matched on strain",
      name: "Matched on name",
      breeder: "Matched on breeder",
      family: "Matched on family",
    }[matchedVia] || null;
  })();

  const onAuthRequired = () => {
    const next = `/strains/${slug}`;
    window.location.href = `/register?next=${encodeURIComponent(next)}`;
  };

  return (
    <section className="mt-10 pt-6 border-t border-[var(--border)]"
             data-testid="strain-trending-strip">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="label-eyebrow mb-1 inline-flex items-center gap-2"
               style={{ color: "var(--gold)" }}>
            <Activity className="w-3 h-3" /> Live · Forge Feed
            {matchLabel && (
              <span className="font-mono text-[9px] uppercase tracking-[0.18em] ml-1"
                    style={{ color: "var(--text-muted)" }}>
                · {matchLabel}
              </span>
            )}
          </div>
          <h2 className="font-serif text-2xl md:text-3xl tracking-tighter"
              style={{ color: "var(--text)" }}>
            Now Trending Around This Strain
          </h2>
        </div>
        <Link to="/forge-feed"
              className="btn-outline !py-1.5 !px-3 text-[11px] inline-flex items-center gap-1.5"
              data-testid="strain-trending-view-feed">
          View Forge Feed <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      {items.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4"
             data-testid="strain-trending-grid">
          {items.map((it) => (
            <FeedCard key={it.id} item={it} isAuthed={!!user}
                      onAuthRequired={onAuthRequired} />
          ))}
        </div>
      ) : (
        <div className="bg-[var(--surface)] border border-[var(--border)] p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-4"
             style={{ borderRadius: 2 }}
             data-testid="strain-trending-empty">
          <Sparkles className="w-6 h-6 shrink-0" style={{ color: "var(--gold)", opacity: 0.6 }} />
          <p className="text-sm flex-1" style={{ color: "var(--text-2)" }}>
            No trending activity yet. Follow{" "}
            <Link to="/forge-feed" className="underline hover:text-[var(--gold)]"
                  style={{ color: "var(--text)" }}
                  data-testid="strain-trending-empty-feed-link">Forge Feed</Link>
            {" "}for new drops, grow logs, lineage updates, and community activity.
          </p>
          {!user && (
            <Link to="/register?next=%2Fforge-feed"
                  className="btn-primary !py-1.5 !px-3 text-[11px] shrink-0"
                  data-testid="strain-trending-empty-cta">
              Create free account
            </Link>
          )}
        </div>
      )}
    </section>
  );
}
